import { useCallback, useRef } from 'react';
import type React from 'react';

import { getCanvasPointFromClientPoint, getPointerEventPressure } from '../../drawingEditorEventUtils';
import { getPagePositionFromClientPosition } from '../../../../../utils/domUtil';
import { createGetRelativePositionFromEventFn } from '../../../../../utils/dom/domEventUtil';
import type { Point } from '../../../../../../common/maths/geometry/pointTypes';

const DEFAULT_POINT: Point = { x: 0, y: 0 };
const DEFAULT_RECT = new DOMRect();

/**
 * Converts pointer client coordinates to page coordinates for relative position mapping.
 */
const getPointerEventPagePosition = (event: PointerEvent): Point | null =>
    (getPagePositionFromClientPosition({ x: event.clientX, y: event.clientY }) as Point | null) || DEFAULT_POINT;

const getRelativePositionFromPointerEvent = createGetRelativePositionFromEventFn(getPointerEventPagePosition);

type UseInputCoordinateMapperArgs = {
    drawingCanvasRootRef: React.RefObject<HTMLElement>;
    inputLayerRef: React.RefObject<HTMLElement>;
    canvasScale?: number;
    viewBoxX?: number;
    viewBoxY?: number;
    zoomScale?: number;
};

/**
 * Maps a pointer event to coordinates relative to the input layer.
 */
const getRelativeViewportPoint = ({
    event,
    inputLayerNode,
}: {
    event: PointerEvent;
    inputLayerNode?: HTMLElement | null;
}): Point => {
    if (!inputLayerNode) return DEFAULT_POINT;

    return getRelativePositionFromPointerEvent(event, inputLayerNode) || DEFAULT_POINT;
};

/**
 * Produces normalized pointer coordinates in both viewport space and drawing canvas space.
 */
const useInputCoordinateMapper = ({
    drawingCanvasRootRef,
    inputLayerRef,
    canvasScale = 1,
    viewBoxX = 0,
    viewBoxY = 0,
    zoomScale = 1,
}: UseInputCoordinateMapperArgs) => {
    const cachedSvgRootRef = useRef<HTMLElement | null>(null);
    const cachedSvgElementRef = useRef<SVGElement | null>(null);

    /**
     * Resolves and caches the drawing SVG element, refreshing only when the root node changes.
     */
    const getCachedSvgElement = useCallback((): SVGElement | null => {
        const currentRoot = drawingCanvasRootRef.current || null;
        const cachedSvgElement = cachedSvgElementRef.current;

        if (cachedSvgRootRef.current === currentRoot && cachedSvgElement && currentRoot?.contains(cachedSvgElement)) {
            return cachedSvgElement;
        }

        cachedSvgRootRef.current = currentRoot;
        cachedSvgElementRef.current = (currentRoot?.querySelector('.DrawingEditorCanvasSvg') as SVGElement) || null;
        return cachedSvgElementRef.current;
    }, [drawingCanvasRootRef]);

    const mapPointerEvent = useCallback(
        (event: PointerEvent) => {
            const clientPoint = {
                x: event.clientX,
                y: event.clientY,
            };

            const viewportPoint = getRelativeViewportPoint({
                event,
                inputLayerNode: inputLayerRef.current,
            });

            const svgElement = getCachedSvgElement();
            const drawingEditorClientRect = svgElement?.getBoundingClientRect() || DEFAULT_RECT;

            const canvasPoint = getCanvasPointFromClientPoint({
                clientX: event.clientX,
                clientY: event.clientY,
                pressure: getPointerEventPressure(event),
                drawingEditorClientRect,
                canvasScale,
                viewBoxX,
                viewBoxY,
                zoomScale,
            });

            return {
                clientPoint,
                viewportPoint,
                canvasPoint,
            };
        },
        [canvasScale, getCachedSvgElement, inputLayerRef, viewBoxX, viewBoxY, zoomScale],
    );

    return {
        mapPointerEvent,
    };
};

export default useInputCoordinateMapper;

export { getRelativeViewportPoint };
